import moment from 'moment'
import { isEmpty, pick } from 'lodash'

import pMemoize from './pMemoize'
import { axiosInstance as axios, rawAxiosInstance } from './requester'
import { PrefixedStorageCache } from './cache'
import { getDomain, sendMessage } from './functions'
import { getUserTier, isPaidUser } from './freeVersionBlock'
import { getLanguageCode } from './translation'
import {
  API_BASE_URL,
  API_KW_RELEVANCY_URL,
  KEYWORD_METRICS,
  PAGE_SPEED_INSIGHT_URL,
  skipAuthPaths,
  UBERSUGGEST_VERSION,
} from './consts.mjs'
import {
  getLoggedUser,
  signOut,
  generate_ubs_app_header,
  text2sha,
} from './userAuth'
import { getDailyReportInfo, setDailyReportInfo } from './storage'
import { isGlobal } from './locations'

class SessionStorageCache extends PrefixedStorageCache {
  constructor() {
    super(`${UBERSUGGEST_VERSION}:api:`)
  }
}

pMemoize.Cache = SessionStorageCache

const defaultKeyResolver =
  (path) =>
  (...args) => {
    const tier = getUserTier()

    return [path, ...args.map((e) => JSON.stringify(e)), tier].join(':')
  }

const defaultTTLResolver =
  (deltaMin = 30) =>
  () =>
    moment().utc().add(deltaMin, 'm').unix()

const makeGlobalLocIdNull = (locId) => (isGlobal(locId) ? undefined : locId)
export const waitFor = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const DEFAULT_NUMBER_OF_API_CALL_RETRIES = 5

export const queryDomainKeywords = pMemoize(
  (
    domain,
    locId,
    language,
    filter = {},
    sortby = undefined,
    type = 'organic',
    increasedLimit = false,
    previousKey = 0,
    dashboard = undefined, // It is used for keyword suggestions v2 when it's true
    achievableSdDomainFilter = undefined,
  ) =>
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: '/domain_keywords',
      params: {
        domain,
        locId,
        language,
        searchType: type,
        increasedLimit,
        filter,
        previousKey,
        dashboard,
        achievableSdDomainFilter,
        sortby,
      },
    }),
  defaultKeyResolver('domain_keywords'),
  defaultTTLResolver(),
  (data, args) => {
    if (
      Object.keys(args[3]).length === 0 && // filter param
      !args[9] && // achievableSdDomainFilter param
      !args[4]?.includes('sd') && // sortby param
      !args[10] // disable cache param
    )
      return true // cache only if no filters, rankFilters are applied and not sort by sd
    return false
  },
)

export const queryTopSeoOpportunity = (projectId) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `/projects/${projectId}/seo_opportunities`,
  })

export const dismissOpportunity = (projectId, opportunities) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: `/projects/${projectId}/seo_opportunities/mark/dismissed`,
    data: opportunities,
  })

export const doneOpportunity = (projectId, opportunities) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: `/projects/${projectId}/seo_opportunities/mark/done`,
    data: opportunities,
  })

export const approveOpportunity = (projectId, opportunities, status) =>
  axios({
    withCredentials: true,
    method: status ? 'delete' : 'post',
    baseURL: API_BASE_URL,
    url: `/projects/${projectId}/seo_opportunities/mark/approved`,
    data: opportunities,
  })

export const queryDomainOverview = pMemoize(
  (domain, locId, language, withKeywords = true) =>
    axios({
      method: 'get',
      baseURL: API_BASE_URL,
      withCredentials: true,
      url: '/domain_overview',
      params: {
        domain,
        locId,
        language,
        withKeywords,
      },
    }),
  defaultKeyResolver('domain_overview'),
  defaultTTLResolver(),
)

export const queryPageOverview = pMemoize(
  (page, locId, language, withKeywords = true) =>
    axios({
      method: 'get',
      baseURL: API_BASE_URL,
      withCredentials: true,
      url: '/page_overview',
      params: {
        page,
        locId,
        language,
        withKeywords,
      },
    }),
  defaultKeyResolver('page_overview'),
  defaultTTLResolver(),
)

export const querySerpAnalysis = (refresh = false) =>
  pMemoize(
    (keyword, locId, language, limit) =>
      axios({
        method: 'get',
        baseURL: API_BASE_URL,
        withCredentials: true,
        url: '/serp_analysis',
        params: {
          keyword,
          locId,
          language,
          refresh,
          limit,
        },
      }),
    defaultKeyResolver('serp_analysis'),
    defaultTTLResolver(),
    () => true,
    {
      skipCachedValue: refresh,
    },
  )

export const getPageSpeedInsight = (useCache) =>
  pMemoize(
    (domain) => {
      const url = getDomain(domain, 'origin_url')
      const queryParams1 = {
        url,
        strategy: 'desktop',
      }
      const queryParams2 = {
        url,
        strategy: 'mobile',
      }

      const desktopPromise = rawAxiosInstance({
        method: 'get',
        url: PAGE_SPEED_INSIGHT_URL,
        params: queryParams1,
        timeout: 1000 * 60 * 2, // set TTL as 2 mins
      })
        .then((res) => res.data)
        .catch((error) => error.response.data)

      const mobilePromise = waitFor(1500).then(() =>
        rawAxiosInstance({
          method: 'get',
          url: PAGE_SPEED_INSIGHT_URL,
          params: queryParams2,
          timeout: 1000 * 60 * 2, // set TTL as 2 mins
        })
          .then((res) => res.data)
          .catch((error) => error.response.data),
      )

      return Promise.all([desktopPromise, mobilePromise]).then(
        ([desktop, mobile]) => {
          const data = {}
          if (desktop) {
            if (desktop.error) {
              data.DESKTOP = { error: desktop.error }
            } else {
              data.DESKTOP = {
                labData: pick(desktop.lighthouseResult.audits, [
                  'cumulative-layout-shift',
                  'first-contentful-paint',
                  'interactive',
                  'largest-contentful-paint',
                  'speed-index',
                  'total-blocking-time',
                ]),
                opportunities: pick(desktop.lighthouseResult.audits, [
                  'unused-css-rules',
                  'unused-javascript',
                ]),
                originData: pick(desktop.originLoadingExperience, [
                  'metrics',
                  'overall_category',
                ]),
              }
            }
          }

          if (mobile) {
            if (mobile.error) {
              data.MOBILE = { error: mobile.error }
            } else {
              data.MOBILE = {
                labData: pick(mobile.lighthouseResult.audits, [
                  'cumulative-layout-shift',
                  'first-contentful-paint',
                  'interactive',
                  'largest-contentful-paint',
                  'speed-index',
                  'total-blocking-time',
                ]),
                opportunities: pick(mobile.lighthouseResult.audits, [
                  'unused-css-rules',
                  'unused-javascript',
                ]),
                originData: pick(mobile.originLoadingExperience, [
                  'metrics',
                  'overall_category',
                ]),
              }
            }
          }
          return data
        },
      )
    },
    (domain) => domain,
    defaultTTLResolver(60 * 24 * 7), // A week TTL
    (data) => !data.error,
    {
      skipCachedValue: !useCache,
    },
  )

export const getPageSpeedInsightFromBE = (
  domain,
  forceUpdate,
  devices,
  cancelToken,
) => {
  return axios({
    method: 'get',
    withCredentials: true,
    baseURL: API_BASE_URL,
    url: '/pagespeed_audit',
    params: { domain, forceUpdate, devices },
    cancelToken,
  })
}

export const startSiteAudit = (
  domain,
  path,
  crawlMaxPages,
  disablePolling = false,
  recrawl = false,
  cancelToken = undefined,
) => {
  return axios({
    method: 'get',
    withCredentials: true,
    baseURL: API_BASE_URL,
    url: '/site_audit',
    params: { domain, path, crawlMaxPages, disablePolling, recrawl },
    cancelToken,
  })
}

export const stopSiteAudit = (domain, path) => {
  return axios({
    method: 'post',
    withCredentials: true,
    baseURL: API_BASE_URL,
    url: '/stop_site_audit',
    data: { domain, path },
  })
}

export const querySiteAudit = (useCache = true, no_alert = false) =>
  pMemoize(
    (domain, path, crawlMaxPages, cancelToken) => {
      return axios({
        method: 'get',
        withCredentials: true,
        baseURL: API_BASE_URL,
        url: '/site_audit_status',
        params: {
          domain,
          path,
          crawlMaxPages,
          no_alert,
        },
        cancelToken,
      })
    },
    defaultKeyResolver('site_audit_status'),
    defaultTTLResolver(),
    ({ data }) => data.result && data.result.done,
    {
      skipCachedValue: !useCache,
    },
  )

export const startPartialSiteAudit = (domain, urls, issue_type) => {
  return axios({
    method: 'post',
    withCredentials: true,
    baseURL: API_BASE_URL,
    url: '/partial_site_audit',
    data: {
      domain,
      urls,
      issue_type,
    },
  })
}

export const queryPartialSiteAudit = pMemoize(
  (domain, crawlMaxPages) => {
    return axios({
      method: 'get',
      withCredentials: true,
      baseURL: API_BASE_URL,
      url: '/site_audit_status',
      params: {
        domain,
        crawlMaxPages,
        no_alert: true, // partial site audit should not trigger alerts
      },
    })
  },
  defaultKeyResolver('site_audit_status'),
  defaultTTLResolver(),
  ({ data }) => data.result && data.result.done,
  {
    skipCachedValue: true,
  },
)

export const querySiteAuditBreakdown = (useCache = true) =>
  pMemoize(
    (domain, path, issue) => {
      return axios({
        method: 'get',
        withCredentials: true,
        baseURL: API_BASE_URL,
        url: '/site_audit_breakdown',
        params: {
          domain,
          path,
          issue,
        },
      })
    },
    defaultKeyResolver('site_audit_breakdown'),
    defaultTTLResolver(),
    () => true,
    {
      skipCachedValue: !useCache,
      omitFields: ['data.result.diff'],
    },
  )

export const queryPagesDiscovered = pMemoize(
  (domain) => {
    return axios({
      method: 'get',
      withCredentials: true,
      baseURL: API_BASE_URL,
      url: '/site_audit_crawled_pages_status',
      params: {
        domain,
      },
    })
  },
  defaultKeyResolver('site_audit_crawled_pages_status'),
  defaultTTLResolver(),
)

export const updateIgnoredIssues = (data) => {
  return axios({
    method: 'post',
    withCredentials: true,
    baseURL: API_BASE_URL,
    url: '/ignored_page_issues',
    data,
  })
}

export const queryDomainTopPages = pMemoize(
  (
    domain,
    locId = 2840,
    language = 'en',
    limit = 10,
    offset = 0,
    sortby = undefined,
    filter = undefined,
  ) =>
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: '/domain_top_pages',
      params: {
        domain,
        language,
        locId,
        limit,
        previousKey: offset,
        sortby,
        filter,
      },
    }),
  defaultKeyResolver('domain_top_pages'),
  defaultTTLResolver(),
)

export const queryContentIdeasPages = pMemoize(
  (
    keyword,
    locId = 2840,
    language = 'en',
    limit = 10,
    offset = 0,
    filters = undefined,
    sortby = undefined,
  ) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/content_ideas_pages',
      data: {
        keywords: [keyword],
        language,
        locId,
        limit,
        previousKey: offset,
        filters,
        sortby,
      },
    }),
  defaultKeyResolver('content_ideas_pages'),
  defaultTTLResolver(),
)

export const getContentIdeasCSV = (
  keyword,
  locId,
  language,
  filters,
  sortby,
) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/content_ideas_pages_csv',
    responseType: 'blob',
    data: {
      keywords: [keyword],
      locId,
      language,
      filters,
      sortby,
    },
  })
}

export const queryPageKeywords = pMemoize(
  (
    url,
    locId = 2840,
    language = 'en',
    filters = {},
    sortby = undefined,
    limit = 10,
    previousKey = 0,
  ) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/page_keywords',
      data: {
        url,
        language,
        locId,
        searchType: 'organic',
        filters,
        sortby,
        limit,
        previousKey,
      },
    }),
  defaultKeyResolver('page_keywords'),
  defaultTTLResolver(),
)

export const queryKeywordInformation = pMemoize(
  ({ keyword, locId = 2840, language = 'en', withGlobalSVBreakdown = false }) =>
    axios({
      method: 'get',
      withCredentials: true,
      baseURL: API_BASE_URL,
      url: '/keyword_info',
      params: {
        keyword,
        language,
        locId: makeGlobalLocIdNull(locId),
        withGlobalSVBreakdown,
      },
    }),
  defaultKeyResolver('keyword_info'),
  defaultTTLResolver(),
)

export const getDomainTopCountries = pMemoize(
  (domain, lang_locs, path) =>
    axios({
      method: 'post',
      withCredentials: true,
      baseURL: API_BASE_URL,
      url: '/domain_top_countries',
      data: { domain, lang_locs, path },
    }),
  defaultKeyResolver('domain_top_countries'),
  defaultTTLResolver(),
)

export const createBacklinksTask = (
  domain,
  mode = 'domain',
  one_per_domain = false,
  order_by = 'domain_inlink_rank',
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'task_backlinks',
      data: {
        domain,
        mode,
        order_by,
        one_per_domain,
      },
    }),
  )

export const getBacklinks = (
  domain,
  mode = 'domain',
  onePerDomain = false,
  limit = 100,
  offset = 0,
  orderBy = 'date_found',
  retry = 5,
) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: '/backlinks',
    params: {
      domain,
      one_per_domain: onePerDomain,
      limit,
      previousKey: offset,
      mode,
      order_by: orderBy,
    },
    retry,
  })

export const createOpportunityTask = (positive_targets, negative_targets) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'task_backlink_opportunity',
      data: {
        positive_targets,
        negative_targets,
      },
    }),
  )

export const getOpportunity = (
  positive_targets,
  negative_targets,
  previousKey,
  limit,
  retry = 5,
) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: `/backlink_opportunity`,
    data: {
      positive_targets,
      negative_targets,
    },
    params: {
      previousKey,
      limit,
    },
    retry,
  })

export const generateOpportunityCSV = (
  tab,
  positive_targets,
  negative_targets,
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'backlink_opportunity_csv',
      responseType: 'blob',
      data: {
        positive_targets,
        negative_targets,
      },
      params: {
        tab,
      },
    }),
  )
export const createAnchorTask = (domain, mode = 'domain') =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'task_anchors',
    data: {
      domain,
      mode,
    },
  })

export const getAnchorTexts = (
  domain,
  mode = 'domain',
  offset = 0,
  limit = 10,
  retry = 3,
) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: '/anchor_texts',
    params: {
      domain,
      mode,
      previousKey: offset,
      limit,
    },
    retry,
  })

export const getDaDistribution = pMemoize(
  (domain, mode) =>
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: '/da_distribution',
      params: {
        domain,
        mode,
      },
    }),
  defaultKeyResolver('anchor_texts'),
  defaultTTLResolver(),
)

export const createLinkingDomainsTask = (
  domain,
  mode = 'domain',
  filter_by = 'new',
  begin_date = '',
  end_date = '',
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'task_linking_domains',
      data: {
        domain,
        mode,
        filter_by,
        begin_date,
        end_date,
      },
    }),
  )

export const getLinkingDomains = ({
  domain,
  mode = 'domain',
  filterBy: filter_by = 'new',
  beginDate: begin_date = '',
  endDate: end_date = '',
  offset = 0,
  limit = 100,
  retry = 5,
}) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: '/linking_domains',
    params: {
      domain,
      mode,
      filter_by,
      begin_date,
      end_date,
      limit,
      previousKey: offset,
    },
    retry,
  })

export const getSemanticSuggestions = pMemoize(
  (keywords, language, locId) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/semantic_suggestions',
      data: { keywords, language, locId },
    }),
  defaultKeyResolver('related_keywords'),
  defaultTTLResolver(),
)

export const queryBacklinksOverview = pMemoize(
  (domain, mode = 'domain') =>
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: '/backlinks_overview',
      params: { domain, mode },
    }),
  defaultKeyResolver('backlinks_overview'),
  defaultTTLResolver(),
)

export const queryDomainCounts = pMemoize(
  (domains) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/domain_counts',
      data: { domains },
    }),
  defaultKeyResolver('domain_counts'),
  defaultTTLResolver(),
)

export const startKeywordSuggestionsInfoTask = pMemoize(
  (keywords, locId, language) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/keyword_suggestions_info',
      data: { keywords, language, locId },
    }),
  defaultKeyResolver('keyword_suggestions_info'),
  defaultTTLResolver(),
  ({ status }) => {
    if (status === 201 || status === 202) return true
    return false
  },
)

export const getKeywordSuggestionsInfo = pMemoize(
  (
    keywords,
    locId,
    language,
    filters = {},
    sortby = '-searchVolume',
    domain = undefined,
    retry = 50,
  ) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/keyword_suggestions_info_task_status',
      data: { keywords, language, locId, filters, sortby, domain },
    }).then((res) => {
      if (!res.data.done && retry > 0) {
        return waitFor(5000).then(() =>
          getKeywordSuggestionsInfo(
            keywords,
            locId,
            language,
            filters,
            sortby,
            domain,
            retry - 1,
          ),
        )
      }
      if (res.data.done) return res
      throw new Error('Please try again later')
    }),
  defaultKeyResolver('keyword_suggestions_info_task_status'),
  defaultTTLResolver(),
  ({ data }, args) => {
    if (
      data.done === true &&
      isEmpty(args[3]) &&
      !args[4]?.includes('sd') &&
      !args[5]
    )
      return true // cache only if no filters and rankFilters are applied and not sort by sd and report is done.
    return false
  },
)

export const getKeywordMatches = pMemoize(
  (
    keywords,
    locId,
    language,
    limit = 500,
    offset = 0,
    sortby = '-searchVolume',
    filters = {},
    domain = undefined,
    query = '',
  ) => {
    return axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `/match_keywords${query ? `?${query}` : ''}`,
      data: {
        keywords,
        locId,
        language,
        sortby,
        limit,
        previousKey: offset,
        filters,
        domain,
      },
    })
  },
  defaultKeyResolver('suggestions'),
  defaultTTLResolver(),
  (data, args) => {
    if (isEmpty(args[6]) && !args[5]?.includes('sd')) return true // cache only if no filters are applied and not sort by sd
    return false
  },
)

// Each of the following projects require Google's auth, if the user
// followed getGoogleSignInURL a cookie should have already been set with the backend

// Helper to sync login status on 401 error

export const syncStatus = (request) => {
  return request
    .then((response) => Promise.resolve(response))
    .catch((error) => {
      const isInvalidUserSession =
        error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.error === 'INVALID_USER_SESSION'

      if (
        error.response &&
        error.response.status === 401 &&
        !isInvalidUserSession &&
        !skipAuthPaths.some((path) => window.location.pathname.includes(path))
      ) {
        signOut(`/${getLanguageCode()}/login?next=${window.location.href}`)
      }
      return Promise.reject(error)
    })
}

export const login = (email, password, remember_me, eventId) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'user/login',
      params: {
        remember_me,
      },
      data: {
        email,
        password,
        event_id: eventId,
      },
      _skipAuth: true,
    }),
  )

export const register = async (
  email,
  password,
  language = 'en',
  recurly_coupon = undefined,
  country = 'United States',
  city = 'New York',
  eventId = undefined,
) =>
  syncStatus(
    rawAxiosInstance({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'user/signup',
      headers: {
        'X-UBS-APP': await generate_ubs_app_header(),
      },
      data: {
        email,
        password,
        lang: language,
        recurly_coupon,
        country,
        city,
        event_id: eventId,
      },
    }),
  )

export const enterpriseSignup = async ({
  email,
  password,
  referral_code,
  lang = 'en',
}) =>
  syncStatus(
    rawAxiosInstance({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/enterprise/signup',
      headers: {
        'X-UBS-APP': await generate_ubs_app_header(),
      },
      data: {
        email,
        password,
        referral_code,
        lang,
      },
    }),
  )

export const forgotPassword = (email, lang) =>
  rawAxiosInstance({
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'user/forgot-password',
    params: { email, lang },
  })

export const resetPassword = (email, password, code) =>
  rawAxiosInstance({
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'user/reset-password',
    data: { email, password, code },
  })

export const changeEmail = (newEmail, password) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'user/change_email',
      data: {
        lang: getLanguageCode(),
        password,
        new_email: newEmail,
      },
    }),
  )

export const closeSmaterThumbPrompt = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'patch',
      baseURL: API_BASE_URL,
      url: 'user',
      data: {
        opportunities_thumbs_banner_closed: true,
      },
    }),
  )

export const changeUserEmail = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/change_user_email',
      data,
    }),
  )

export const deleteUserEmail = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/delete_user',
      data,
    }),
  )

export const changeLoginMethodToEmailByCS = (email) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'clientsuccess/change_auth_system_to_email',
    data: {
      email,
    },
  })

export const bulkAccountCreate = (formData) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'clientsuccess/bulk_account_create',
    data: formData,
    responseType: 'blob',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

export const syncSubscriptionWithRecurly = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/sync_subscription_with_recurly',
      data,
    }),
  )

export const removeUserFromSupressList = (email) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/delete_user_from_suppressed_list',
      data: { email },
    }),
  )

export const removeBillingInfo = (email) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `clientsuccess/remove_billing_info`,
      data: { email },
    }),
  )

export const changeLoginMethodToEmail = (password) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'user/change-auth-system-to-email',
      data: {
        password,
        password_repeat: password,
      },
    }),
  )

export const getUser = ({
  city,
  country,
  countryCode,
  timezone,
  regionName,
} = {}) =>
  syncStatus(
    rawAxiosInstance({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: 'user',
      params: {
        preferred_locale: getLanguageCode(),
        city,
        country,
        countryCode,
        timezone,
        regionName,
      },
    }).catch((e) => {
      if (e.response) {
        return { headers: e.response.headers }
      }
      return null
    }),
  )

export const patchUser = (data) =>
  syncStatus(
    rawAxiosInstance({
      withCredentials: true,
      method: 'patch',
      baseURL: API_BASE_URL,
      url: 'user',
      data,
    }),
  )

export const updateUserPreferences = (data) =>
  syncStatus(
    rawAxiosInstance({
      withCredentials: true,
      method: 'patch',
      baseURL: API_BASE_URL,
      url: 'user/preferences',
      data,
    }),
  )

export const getProjects = pMemoize(
  () =>
    syncStatus(
      axios({
        withCredentials: true,
        method: 'get',
        baseURL: API_BASE_URL,
        url: 'projects',
      }),
    ),
  defaultKeyResolver('projects'),
  defaultTTLResolver(0.5),
)

const createProjectErrorMessages = {
  400: {
    'Exceeded maximum amount of projects for a single user.':
      'MAXIMUM_AMOUNT_OF_PROJECTS',
    'Project with this domain already exists.': 'PROJECT_ALREADY_EXISTS',
    default: 'UNEXPECTED_ERROR',
  },
  500: {
    default: 'UNEXPECTED_ERROR',
  },
}

export const createProject = async (projectInfo) => {
  try {
    const response = await syncStatus(
      axios({
        withCredentials: true,
        method: 'post',
        baseURL: API_BASE_URL,
        url: 'projects',
        data: projectInfo,
      }),
    )

    return response
  } catch (error) {
    const { response } = error
    const message =
      createProjectErrorMessages[response.status][response.data.description] ??
      createProjectErrorMessages[response.status].default
    throw new Error(message)
  }
}

export const updateProject = (projectId, updateParameters) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'put',
      baseURL: API_BASE_URL,
      url: `projects/${projectId}`,
      data: updateParameters,
    }),
  )

export const deleteProject = (projectId) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `projects/${projectId}`,
    }),
  )

export const getProject = (projectId) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `projects/${projectId}`,
    }),
  )

export const getAvailableKeywords = pMemoize(
  (domain, langLocs) =>
    syncStatus(
      axios({
        withCredentials: true,
        method: 'post',
        baseURL: API_BASE_URL,
        url: `projects/available_keywords`,
        data: { domain, langLocs },
      }),
    ),
  defaultKeyResolver('position_tracking_available_keywords'),
  defaultTTLResolver,
)

export const getProjectInfo = async ({
  projectId,
  startDate,
  endDate,
  locId,
  language,
  device,
  retry = 30,
}) => {
  const response = await axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `projects/${projectId}/position_info`,
    params: {
      startDate,
      endDate,
      locId,
      language,
      device,
    },
  })

  if (response.status === 202 && retry > 0) {
    return waitFor(5000).then(() =>
      getProjectInfo({
        projectId,
        startDate,
        endDate,
        locId,
        language,
        device,
        retry: retry - 1,
      }),
    )
  }

  if (response.status === 200) {
    return response
  }

  return false
}

export const getKeywordPositionInfo = pMemoize(
  (projectId, keyword, startDate, endDate, locId, language, device) =>
    syncStatus(
      axios({
        withCredentials: true,
        method: 'get',
        baseURL: API_BASE_URL,
        url: `projects/${projectId}/keyword_position_info`,
        params: {
          keyword,
          startDate,
          endDate,
          locId,
          language,
          device,
        },
      }),
    ),
  defaultKeyResolver('position_tracking_keyword_info'),
  defaultTTLResolver(7),
)

export const createCompetitorAnalysisReport = (
  domain,
  locId,
  language,
  competitors = undefined,
  limit = 20,
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'competitors',
      data: {
        domain,
        language,
        locId,
        competitors,
        limit,
      },
    }),
  )

export const createDashboardCompetitorAnalysisReport = pMemoize(
  (
    projectId,
    domain,
    locId,
    language,
    competitors = undefined,
    include_suggested = true,
    limit = 20,
  ) =>
    syncStatus(
      axios({
        withCredentials: true,
        method: 'post',
        baseURL: API_BASE_URL,
        url: 'dashboard_competitors',
        data: {
          projectId,
          domain,
          language,
          locId,
          competitors,
          include_suggested,
          limit,
        },
      }),
    ),
  defaultKeyResolver('dashboard_competitors'),
  defaultTTLResolver(10),
)

export const getCompetitorAnalysisReport = pMemoize(
  (id) =>
    syncStatus(
      axios({
        withCredentials: true,
        method: 'get',
        baseURL: API_BASE_URL,
        url: `competitors/${id}`,
      }),
    ),
  defaultKeyResolver('competitors'),
  defaultTTLResolver(10),
  ({ data }) => data && !data.pendingData,
)

export const getCommonKeywords = (
  domain,
  competitor,
  locId,
  language,
  previousKey = 0,
  limit = 10,
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: 'common_keywords',
      params: {
        domain,
        competitor,
        locId,
        language,
        previousKey,
        limit,
      },
    }),
  )

export const getGapKeywords = (
  domain,
  competitor,
  locId,
  language,
  previousKey = 0,
  limit = 10,
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: 'gap_keywords',
      params: {
        domain,
        competitor,
        locId,
        language,
        previousKey,
        limit,
      },
    }),
  )

export const getGapKeywordsCSV = (domain, competitor, locId, language) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'gap_keywords_csv',
    responseType: 'blob',
    params: {
      domain,
      competitor,
      locId,
      language,
    },
  })

export const getCommonKeywordsCSV = (domain, competitor, locId, language) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'common_keywords_csv',
    responseType: 'blob',
    params: {
      domain,
      competitor,
      locId,
      language,
    },
  })

export const getAlerts = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: 'user/alerts',
    }),
  )

export const getAlert = (id) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `user/alerts/${id}`,
    }),
  )

export const deleteAlert = (id) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `user/alerts/${id}`,
    }),
  )

export const markAlertsAsRead = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'put',
      baseURL: API_BASE_URL,
      url: 'user/alerts',
      data,
    }),
  )

export const getSiteAuditCSV = (domain, path) => {
  return axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: '/site_audit_breakdown_zipped_csv_report',
    responseType: 'blob',
    headers: {
      Accept: 'application/zip',
    },
    params: {
      domain,
      path,
    },
  })
}

export const getDomainKeywordsCSV = (
  domain,
  locId,
  language,
  filter,
  sortby,
  achievableSdDomainFilter = undefined,
  columns = undefined,
  searchType = 'organic',
) => {
  return axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: '/domain_keywords_csv',
    responseType: 'blob',
    params: {
      domain,
      locId,
      language,
      filter,
      sortby,
      achievableSdDomainFilter,
      searchType,
      columns,
    },
  })
}

export const getPageKeywordsCSV = (
  url,
  locId,
  language,
  filters,
  sortby = undefined,
) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/page_keywords_csv',
    responseType: 'blob',
    data: {
      url,
      locId,
      language,
      filters,
      sortby,
    },
  })
}

export const getDomainTopPagesCSV = (
  domain,
  locId,
  language,
  sortby = undefined,
  filter = undefined,
  searchType = 'organic',
) => {
  return axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: '/domain_top_pages_csv',
    responseType: 'blob',
    params: {
      domain,
      locId,
      language,
      searchType,
      sortby,
      filter,
    },
  })
}

export const activityHistory = pMemoize(
  (page_address, type = 'REPORT_CREATED', data = undefined) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'track_activity',
      data: {
        type,
        data,
      },
    }),
  defaultKeyResolver('track_activity'),
  defaultTTLResolver(),
  (res) => !res.data.error && !isNaN(res.data.reports_used),
)

export const logUserActivity = (page_address, type = 'REPORT_CREATED') => {
  // TODO remove after connection with BE
  if (!isPaidUser()) return Promise.resolve(true)
  return activityHistory(page_address, type).then((res) => {
    if (!res.headers['x-from-cache']) {
      setDailyReportInfo(res.data)
    }
    return getDailyReportInfo()
  })
}

export const getUserLimits = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: 'user/limits',
    }),
  )

export const updateBillingInformation = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'update_invoicing_settings',
      data,
    }),
  )

export const getProjectHistoricData = (dataType) =>
  pMemoize(
    (projectId, from_date, to_date) =>
      syncStatus(
        axios({
          withCredentials: true,
          method: 'get',
          baseURL: API_BASE_URL,
          url: `projects/${projectId}/historical/${dataType}`,
          params: {
            from_date,
            to_date,
          },
        }),
      ),
    defaultKeyResolver(`projects_historical_${dataType}`),
    defaultTTLResolver,
  )

export const clearProjectHistoricSearchTraffic = () => {
  const cache = new SessionStorageCache()
  cache.deleteAll('position_tracking_search_traffic') // clear all
}

export const getGoogleServiceStatus = (projectId) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `projects/${projectId}/google_services_status`,
    }),
  )

export const getAnalyticsProfiles = (projectId) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `projects/${projectId}/google_analytics_profiles`,
    }),
  )

export const getProjectAnalyticsData = pMemoize(
  (
    projectId,
    startDate,
    endDate,
    device,
    dimension,
    compareStartDate,
    compareEndDate,
  ) =>
    syncStatus(
      axios({
        withCredentials: true,
        method: 'get',
        baseURL: API_BASE_URL,
        url: `projects/${projectId}/google_analytics`,
        params: {
          startDate,
          endDate,
          device,
          dimension,
          compareStartDate,
          compareEndDate,
        },
      }),
    ),
  defaultKeyResolver('google_analytics'),
  defaultTTLResolver,
  ({ data }) => data.result?.report?.length > 0,
)

export const setAnalyticsProfile = (projectId, profile) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'put',
      baseURL: API_BASE_URL,
      url: `projects/${projectId}/google_analytics_profile`,
      data: {
        profile,
      },
    }),
  )

export const deleteUser = () =>
  rawAxiosInstance({
    withCredentials: true,
    method: 'delete',
    baseURL: API_BASE_URL,
    url: `user`,
  })

export const generateLeads = (
  data,
  {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_term,
    utm_content,
    last_campaign_source,
    ref,
    ref_ext,
  } = {},
) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'grow_my_traffic',
    data,
    params: {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      last_campaign_source,
      ref,
      ref_ext,
    },
  })

export const getOvertimeHistory = pMemoize(
  (
    domain,
    mode = 'domain',
    doFollow = undefined,
    dateFrom = undefined,
    dateTo = undefined,
  ) =>
    axios({
      method: 'get',
      withCredentials: true,
      baseURL: API_BASE_URL,
      url: 'overtime',
      params: {
        domain,
        mode,
        do_follow: doFollow,
        date_from: dateFrom,
        date_to: dateTo,
      },
    }),
  defaultKeyResolver('overtime'),
  defaultTTLResolver,
)

export const getNewLostLinkingDomains = pMemoize(
  (domain, mode = 'domain', dateFrom = undefined, dateTo = undefined) =>
    axios({
      method: 'get',
      withCredentials: true,
      baseURL: API_BASE_URL,
      url: 'new_lost_linking_domains',
      params: {
        domain,
        mode,
        date_from: dateFrom,
        date_to: dateTo,
      },
    }),
  defaultKeyResolver('new_lost_linking_domains'),
  defaultTTLResolver,
)

export const checkout = ({
  plan,
  subscriptionType,
  currency,
  coupon,
  addOn,
  noTrial,
  item,
  paypal_account_hash,
  threeDSecureToken = undefined,
  eventId,
}) =>
  axios({
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'checkout',
    withCredentials: true,
    params: {
      plan,
      subscriptionType,
      currency,
      preferred_locale: getLanguageCode(),
      coupon,
      add_on: addOn,
      no_trial: noTrial,
      item,
      paypal_account_hash,
      threeDSecureToken,
      event_id: eventId,
    },
  })

export const preCheckout = ({
  paymentMethod,
  currency,
  threeDSecureToken,
  eventId,
}) =>
  axios({
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'pre_checkout',
    withCredentials: true,
    params: {
      paymentMethod,
      currency,
      preferred_locale: getLanguageCode(),
      threeDSecureToken,
      event_id: eventId,
    },
  })

export const processCheckout = async ({
  paymentMethod,
  plan,
  subscriptionType = 'recurly',
  currency = 'USD',
  threeDSecureToken = undefined,
  coupon = undefined,
  addOn = undefined,
  noTrial = undefined,
  item = undefined,
  skipPreCheckout = false,
  eventId,
}) => {
  const preCheckoutResult = skipPreCheckout
    ? null
    : await preCheckout({
        paymentMethod: paymentMethod.id,
        currency,
        threeDSecureToken,
        eventId,
      })
  const paypal_account_hash =
    paymentMethod?.type === 'paypal'
      ? await text2sha({
          algorithm: 'SHA-1',
          text: `${paymentMethod.email}:${paymentMethod.payer_id}`,
        })
      : null
  const checkoutResult = await checkout({
    plan,
    subscriptionType,
    currency,
    coupon,
    addOn,
    noTrial,
    item,
    paypal_account_hash,
    threeDSecureToken: skipPreCheckout ? threeDSecureToken : undefined,
    eventId,
  })

  return preCheckoutResult || checkoutResult
}

export const updatePlan = (
  plan,
  item = undefined,
  retry = 3,
  threeDSecureToken = undefined,
) =>
  rawAxiosInstance({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'update_plan',
    data: {
      plan,
      item,
      threeDSecureToken,
    },
    retry,
    retryDelay: 200,
  }).catch((error) => {
    sendMessage({ ...error?.response?.data })
    throw error?.response?.data
  })

export const updateUserPaymentMethod = (
  tokenId,
  threeDSecureToken = undefined,
  method = 'put',
) => {
  return axios({
    withCredentials: true,
    method,
    baseURL: API_BASE_URL,
    url: '/user/payment_method',
    data: {
      tokenId,
      threeDSecureToken,
    },
  })
}

export const renewSubscription = () =>
  axios({
    method: 'put',
    baseURL: API_BASE_URL,
    url: 'subscription/reactivate',
    withCredentials: true,
  })

export const cancelSubscription = () =>
  axios({
    method: 'delete',
    baseURL: API_BASE_URL,
    url: 'subscription/cancel',
    withCredentials: true,
  })

export const getSubscription = (retry = 3) =>
  rawAxiosInstance({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'subscription',
    retry,
    retryDelay: 200,
  })

export const getPlans = (subscriptionType = 'recurly') =>
  rawAxiosInstance({
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'tiers',
    params: {
      subscriptionType,
    },
  })

export const ipLookUp = pMemoize(
  () =>
    axios({
      method: 'get',
      url: '/geolocation',
      baseURL: API_BASE_URL,
    }).catch((error) => {
      console.error(error)
      return null
    }),
  defaultKeyResolver('get_ip'),
  defaultTTLResolver(60 * 24 * 7),
)

export const getNotificationSettings = () =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'notification_settings',
  })

export const patchNotificationSettings = (data) =>
  axios({
    withCredentials: true,
    method: 'patch',
    baseURL: API_BASE_URL,
    url: 'notification_settings',
    data,
  })

export const sendCancelationFeedback = (category, notes, status) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'delete_reason',
    data: {
      category,
      notes,
      status,
    },
  })

export const getKeywordLists = () =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `keywords_lists`,
  })

export const createKeywordList = (name, keywords) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'keywords_lists',
      data: {
        name,
        append: keywords,
      },
    }),
  )

export const updateKeywordList = (id, name, append, remove) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'patch',
      baseURL: API_BASE_URL,
      url: `keywords_lists/${id}`,
      data: {
        name,
        append,
        remove,
      },
    }),
  )

export const updateKeywordLists = (ids, append) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'patch',
      baseURL: API_BASE_URL,
      url: `keywords_lists`,
      data: {
        ids,
        update: {
          append,
        },
      },
    }),
  )

export const deleteKeywordList = (id) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `keywords_lists/${id}`,
    }),
  )

export const getKeywordListData = (id) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `keywords_lists/${id}`,
  })

export const validateSite = pMemoize(
  (site, is_domain, crawlable = false) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: crawlable
        ? 'validate_site?check_is_crawlable=true'
        : 'validate_site',
      data: {
        site,
        is_domain,
      },
    }),
  defaultKeyResolver('validate_site'),
  defaultTTLResolver(30000),
)

export const getTrafficValue = (domain, retry = 5) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `/get_domain_traffic_value_task_status`,
    params: {
      domain,
    },
    retry,
    retryDelay: 2500,
  })

export const getGoogleSuggestionsFromBE = pMemoize(
  async (keywords, language, country) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/google_suggestions',
      data: { keywords, language, country },
      headers: {
        'X-UBS-APP': await generate_ubs_app_header(),
      },
    }),
  defaultKeyResolver('google_suggestions'),
  defaultTTLResolver(60),
)

export const getAddons = () =>
  rawAxiosInstance({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `addons`,
  })

export const purchaseAddons = (add_on_code, projectId = null) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'put',
      baseURL: API_BASE_URL,
      url: `/subscription/addons/${add_on_code}`,
      params: projectId
        ? {
            projectId,
          }
        : null,
    }).catch((error) => {
      sendMessage({ ...error?.response?.data })
      throw error?.response?.data
    }),
  )

export const removeAddons = (add_on_code, projectId = null) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `subscription/addons/${add_on_code}`,
      params: projectId
        ? {
            projectId,
          }
        : null,
    }),
  )

export const getCouponInfo = (coupon_code) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `coupons/${coupon_code}`,
    }),
  )

export const getKeywordEntity = pMemoize(
  (domain, loc_id, user_kws = undefined) =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_KW_RELEVANCY_URL,
      url: '/kw_entity',
      data: {
        domain,
        loc_id,
        user_kws,
      },
      headers: getLoggedUser()
        ? {
            'User-Id': getLoggedUser().id,
          }
        : {},
      retry: 3, // sometimes we get
      retryIf: (error) =>
        error?.message === 'Network Error' || error?.response?.status >= 500,
    }),
  defaultKeyResolver('kw_entity'),
  defaultTTLResolver(),
)

export const getKeywordSimilarity = (
  keywords,
  domain,
  competitor,
  loc_id,
  kw_num = 5000,
) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_KW_RELEVANCY_URL,
    url: '/get_similarity',
    data: {
      kws: keywords,
      user_domain: domain,
      competitor_domain: competitor,
      loc_id,
      kw_num,
    },
    headers: getLoggedUser()
      ? {
          'User-Id': getLoggedUser().id,
        }
      : {},
  })

export const signupMultiUsers = (
  code,
  password,
  lang,
  country = 'United States',
  city = 'New York',
  eventId = undefined,
) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `/mu/signup`,
      data: {
        code,
        password,
        lang,
        country,
        city,
        event_id: eventId,
      },
    }),
  )

export const getMultiUsersInAccount = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `/mu/users`,
    }),
  )

export const inviteNewUser = (email) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `/mu/invites`,
      data: {
        email,
      },
    }),
  )

export const getInvitedUsers = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `/mu/invites`,
    }),
  )

export const deleteInvitedUser = (email) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `/mu/invites/${email}`,
    }),
  )

export const removeMultiUser = (userId) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `/mu/users/${userId}`,
    }),
  )

export const removeMeFromAccount = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'delete',
      baseURL: API_BASE_URL,
      url: `/mu/user`,
    }),
  )

export const getMainUserInfo = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `/mu/user`,
    }),
  )

export const submitFeatureFeedbck = (data) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: `/user/feature_feedback`,
    data,
  })

export const resendVerificationEmail = (data) =>
  axios({
    withCredentials: false,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/user/resend_verification_email',
    data,
  })

export const migrateOldLifetime = () =>
  axios({
    method: 'put',
    baseURL: API_BASE_URL,
    url: 'subscription/migrate_old_lifetime',
    withCredentials: true,
  })

export const getSiteData = pMemoize(
  (domain, lang = 'en') =>
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: `/onboarding/site_data`,
      params: {
        domain,
        lang,
      },
    }),
  defaultKeyResolver('onboarding/site_data'),
  defaultTTLResolver(),
  ({ data }) => !!data.done,
)

export const getSearchHistory = (workspaceId) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `/search_history?workspace_id=${workspaceId}`,
  })

export const saveSearchHistory = (workspaceId, data) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/search_history',
    params: workspaceId ? { workspace_id: workspaceId } : {},
    data,
  })

export const createKeywordsSdTask = (keywords, isSD = true) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'keyword_metrics',
    data: { keywords, metric: isSD ? KEYWORD_METRICS.SD : KEYWORD_METRICS.SI },
  })

export const getKeywordsSdReport = (reportIds) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `keyword_metrics?reportIds=${reportIds.join(',')}`,
  })

export const getLocationSuggestions = pMemoize(
  (query, lang, signal) =>
    axios({
      method: 'get',
      baseURL: API_BASE_URL,
      withCredentials: true,
      url: '/locations/suggest',
      params: {
        query,
        lang,
      },
      signal,
    }),
  defaultKeyResolver('locations_suggest'),
  defaultTTLResolver(),
)

export const getLocationsData = pMemoize(
  (location_ids, lang = 'en') =>
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'locations/details',
      data: {
        location_ids,
        lang,
      },
    }),
  defaultKeyResolver('locations_details'),
  defaultTTLResolver(),
)

export const addBlockedEmailDomains = (domain) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/block_email_domain',
      data: { domain },
    }),
  )

export const removeBlockedEmailDomains = (domain) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/unblock_email_domain',
      data: { domain },
    }),
  )

export const getUserInfoJson = (email) =>
  rawAxiosInstance({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'clientsuccess/get_user_information',
    data: { email },
  })

export const updateAIWWordsCounter = (email, words_used) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `clientsuccess/update_aiw_words_counter`,
      data: { email, words_used },
    }),
  )

export const checkGTMStatus = () =>
  rawAxiosInstance({
    method: 'get',
    url: `https://www.googletagmanager.com/gtm.js?id=GTM-WMJBH6Z`,
  })

export const disconnectGoogleServices = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'put',
      baseURL: API_BASE_URL,
      url: `/disconnect_google_services`,
    }),
  )

export const inquiryCustomPlan = (data) =>
  axios({
    withCredentials: false,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/custom_plan_inquiry',
    data,
  })

export const restoreProject = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `clientsuccess/restore_projects`,
      data,
    }),
  )

export const getResetPasswordLink = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `clientsuccess/forgot_password`,
      data,
    }),
  )

export const verifyUserEmail = (data) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: `clientsuccess/verify_email`,
      data,
    }),
  )

export const lifetimeOffer = () =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: '/accept_lifetime5_offer',
      data: {},
    }),
  )

export const convertToUSD = pMemoize(
  (amount, currency) =>
    axios({
      method: 'get',
      baseURL: API_BASE_URL,
      withCredentials: false,
      url: '/convert_to_usd',
      params: {
        amount,
        currency,
      },
    }),
  defaultKeyResolver('convert_to_usd'),
  defaultTTLResolver(),
)

export const getUserAddOnsJson = (email) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/user_addons',
      data: { email },
    }),
  )

export const deleteUserAddOns = (email, addon_code, project_id) =>
  syncStatus(
    axios({
      withCredentials: true,
      method: 'post',
      baseURL: API_BASE_URL,
      url: 'clientsuccess/remove_addon',
      data: { email, addon_code, project_id },
    }),
  )

export const setEnterpriseReferalLink = ({ method = 'post' }) =>
  axios({
    withCredentials: true,
    method,
    baseURL: API_BASE_URL,
    url: 'enterprise/referral_link',
  })

export const getEnterpriseAdmin = () => {
  return axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'enterprise',
  })
}

export const enterpriseUnsubscribeUsers = (data) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'enterprise/remove_subscriptions',
    data,
  })
}

export const enterpriseAddSubscription = (data) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'enterprise/add_subscriptions',
    data,
  })
}

export const enterpriseReplaceUsers = (data) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'enterprise/replace_users',
    data,
  })
}

export const enterpriseRemoveUsers = (data) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'enterprise/remove_users',
    data,
  })
}

export const enterpriseReplaceUserSubscription = (data) => {
  return axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: 'enterprise/replace_subscriptions',
    data,
  })
}

export const getEnterpriseInvoices = () => {
  return axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'enterprise/invoices',
  })
}

export const createEnterprise = (data) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: `clientsuccess/enterprise`,
    data,
  })

export const deleteEnterprise = (enterpriseId) =>
  axios({
    withCredentials: true,
    method: 'delete',
    baseURL: API_BASE_URL,
    url: `clientsuccess/enterprise/${enterpriseId}`,
  })

export const getEnterpriseList = () =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: 'clientsuccess/enterprise',
  })

export const getEnterpriseDetails = (enterpriseId) =>
  axios({
    withCredentials: true,
    method: 'get',
    baseURL: API_BASE_URL,
    url: `clientsuccess/enterprise/${enterpriseId}`,
  })

export const updateEnterprise = (enterpriseId, data) =>
  axios({
    withCredentials: true,
    method: 'put',
    baseURL: API_BASE_URL,
    url: `clientsuccess/enterprise/${enterpriseId}`,
    data,
  })

export const getKeywordMatchesCSVReport = ({
  keywords,
  locId,
  language,
  limit = 10000,
  offset = 0,
  sortby = '-searchVolume',
  filters = {},
}) =>
  axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/match_keywords_report',
    responseType: 'blob',
    data: {
      keywords,
      locId,
      language,
      sortby,
      limit,
      previousKey: offset,
      filters,
    },
  })

export const getKeywordMatchesCSVReportStatus = async ({
  keywords,
  locId,
  language,
  limit = 10000,
  offset = 0,
  sortby = '-searchVolume',
  filters = {},
  retry = 30,
}) => {
  const response = await axios({
    withCredentials: true,
    method: 'post',
    baseURL: API_BASE_URL,
    url: '/match_keywords_report_status',
    responseType: 'blob',
    data: {
      keywords,
      locId,
      language,
      sortby,
      limit,
      previousKey: offset,
      filters,
    },
  })

  if (response.status === 202 && retry > 0) {
    return waitFor(5000).then(() =>
      getKeywordMatchesCSVReportStatus({
        keywords,
        locId,
        language,
        limit,
        offset,
        sortby,
        filters,
        retry: retry - 1,
      }),
    )
  }

  if (response.status === 200) {
    return response
  }

  return false
}

export const getHistoricalSearchSuggestions = async ({
  termSearch: term_types,
  filterInfo: filters_info,
}) => {
  const response = await syncStatus(
    axios({
      withCredentials: true,
      method: 'get',
      baseURL: API_BASE_URL,
      url: '/search_history',
      params: {
        term_types,
        ...(filters_info && { filters_info }),
      },
    }),
  )

  if (response.status === 200) {
    const { data } = response
    return data?.map((item) => ({
      ...item,
      locId: item.loc_id,
      searchTerms: item.search_terms,
      termType: item.term_type,
      locationInfo: {
        countryIsoCode: item.country_name,
        canonicalName: item.canonical_name,
      },
    }))
  }

  return false
}
